export type CornerRadiiPx = [number, number, number, number];

export type CurveType = "bottom" | "top" | "left" | "right" | "full-arc" | "custom-bezier";
export type CurveDirection = "in" | "out";

// Corner curve types for rounded corners
export type CornerCurveType = "circular" | "smooth" | "cut";

export interface CornerCurveOptions {
  type: CornerCurveType;
  smoothness: number; // 0-1, only used for "smooth" type (squircle)
}

export function normalizeCornerCurve(el: any): CornerCurveOptions {
  const type: CornerCurveType =
    el?.cornerCurveType === "smooth" || el?.cornerCurveType === "cut"
      ? el.cornerCurveType
      : "circular";
  const smoothness = Math.max(0, Math.min(1, Number(el?.cornerSmoothness ?? 0.6)));
  return { type, smoothness };
}

export interface NormalizedCurve {
  enabled: boolean;
  type: CurveType;
  strength: number; // 0..1
  direction: CurveDirection;
  symmetry: boolean;
  bezier?: { c1x: number; c1y: number; c2x: number; c2y: number };
}

function isCurveType(v: unknown): v is CurveType {
  return v === "bottom" || v === "top" || v === "left" || v === "right" || v === "full-arc" || v === "custom-bezier";
}

function isCurveDirection(v: unknown): v is CurveDirection {
  return v === "in" || v === "out";
}

export function normalizeCurve(el: any): NormalizedCurve | null {
  const enabled = !!el?.curveEnabled;
  if (!enabled) return null;

  const type: CurveType = isCurveType(el?.curveType) ? el.curveType : "bottom";
  const direction: CurveDirection = isCurveDirection(el?.curveDirection) ? el.curveDirection : "out";
  const symmetry = el?.curveSymmetry !== false;
  const strength = Math.max(0, Math.min(1, Number(el?.curveStrength ?? 0)));

  const bezierRaw = el?.curveBezier;
  const bezier =
    bezierRaw &&
    Number.isFinite(bezierRaw.c1x) &&
    Number.isFinite(bezierRaw.c1y) &&
    Number.isFinite(bezierRaw.c2x) &&
    Number.isFinite(bezierRaw.c2y)
      ? {
          c1x: Math.max(0, Math.min(1, Number(bezierRaw.c1x))),
          c1y: Math.max(0, Math.min(1, Number(bezierRaw.c1y))),
          c2x: Math.max(0, Math.min(1, Number(bezierRaw.c2x))),
          c2y: Math.max(0, Math.min(1, Number(bezierRaw.c2y))),
        }
      : undefined;

  if (strength <= 0.001) return null;
  return { enabled: true, type, strength, direction, symmetry, bezier };
}

// Helper to draw a single corner with different styles
function drawCorner(
  ctx: CanvasRenderingContext2D | Path2D,
  cx: number, // corner x
  cy: number, // corner y
  r: number,  // radius
  startAngle: number, // 0=top-right, 90=bottom-right, 180=bottom-left, 270=top-left
  opts: CornerCurveOptions
) {
  if (r <= 0) return;

  const isPath2D = ctx instanceof Path2D;
  const lineTo = isPath2D ? (x: number, y: number) => (ctx as Path2D).lineTo(x, y) : (x: number, y: number) => (ctx as CanvasRenderingContext2D).lineTo(x, y);
  const bezierCurveTo = isPath2D
    ? (cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number) => (ctx as Path2D).bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y)
    : (cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number) => (ctx as CanvasRenderingContext2D).bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);

  if (opts.type === "cut") {
    // Chamfer: straight line cut
    // No curve, just line from current point to next edge start
    return; // The lineTo calls in main function handle this
  }

  if (opts.type === "smooth") {
    // Squircle: bezier curve for smoother corners
    // Magic number for approximating quarter circle with bezier
    const k = 0.5522847498 * opts.smoothness + (1 - opts.smoothness) * 0.3;
    const kR = k * r;

    // Calculate start and end points based on angle
    // startAngle: 0=top-right (270° in standard), going clockwise
    let sx: number, sy: number, ex: number, ey: number;
    let cp1x: number, cp1y: number, cp2x: number, cp2y: number;

    switch (startAngle) {
      case 0: // top-right corner
        sx = cx; sy = cy - r;
        ex = cx + r; ey = cy;
        cp1x = cx + kR; cp1y = cy - r;
        cp2x = cx + r; cp2y = cy - kR;
        break;
      case 90: // bottom-right corner
        sx = cx + r; sy = cy;
        ex = cx; ey = cy + r;
        cp1x = cx + r; cp1y = cy + kR;
        cp2x = cx + kR; cp2y = cy + r;
        break;
      case 180: // bottom-left corner
        sx = cx; sy = cy + r;
        ex = cx - r; ey = cy;
        cp1x = cx - kR; cp1y = cy + r;
        cp2x = cx - r; cp2y = cy + kR;
        break;
      case 270: // top-left corner
        sx = cx - r; sy = cy;
        ex = cx; ey = cy - r;
        cp1x = cx - r; cp1y = cy - kR;
        cp2x = cx - kR; cp2y = cy - r;
        break;
      default:
        return;
    }

    bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
    return;
  }

  // Default: circular (arcTo is already handled in main function)
}

export function roundedRectPath(ctx: CanvasRenderingContext2D, w: number, h: number, r: CornerRadiiPx, cornerOpts?: CornerCurveOptions) {
  const [rtl, rtr, rbr, rbl] = r;
  const opts = cornerOpts || { type: "circular", smoothness: 0.6 };

  ctx.beginPath();
  ctx.moveTo(rtl, 0);
  ctx.lineTo(w - rtr, 0);

  // Top-right corner
  if (rtr > 0) {
    if (opts.type === "cut") {
      ctx.lineTo(w, rtr);
    } else if (opts.type === "smooth") {
      drawCorner(ctx, w - rtr, rtr, rtr, 0, opts);
    } else {
      ctx.arcTo(w, 0, w, rtr, rtr);
    }
  } else {
    ctx.lineTo(w, 0);
  }

  ctx.lineTo(w, h - rbr);

  // Bottom-right corner
  if (rbr > 0) {
    if (opts.type === "cut") {
      ctx.lineTo(w - rbr, h);
    } else if (opts.type === "smooth") {
      drawCorner(ctx, w - rbr, h - rbr, rbr, 90, opts);
    } else {
      ctx.arcTo(w, h, w - rbr, h, rbr);
    }
  } else {
    ctx.lineTo(w, h);
  }

  ctx.lineTo(rbl, h);

  // Bottom-left corner
  if (rbl > 0) {
    if (opts.type === "cut") {
      ctx.lineTo(0, h - rbl);
    } else if (opts.type === "smooth") {
      drawCorner(ctx, rbl, h - rbl, rbl, 180, opts);
    } else {
      ctx.arcTo(0, h, 0, h - rbl, rbl);
    }
  } else {
    ctx.lineTo(0, h);
  }

  ctx.lineTo(0, rtl);

  // Top-left corner
  if (rtl > 0) {
    if (opts.type === "cut") {
      ctx.lineTo(rtl, 0);
    } else if (opts.type === "smooth") {
      drawCorner(ctx, rtl, rtl, rtl, 270, opts);
    } else {
      ctx.arcTo(0, 0, rtl, 0, rtl);
    }
  } else {
    ctx.lineTo(0, 0);
  }

  ctx.closePath();
}

// Helper to draw corner on Path2D
function drawCornerPath2D(
  p: Path2D,
  cx: number, cy: number,
  r: number,
  startAngle: number,
  opts: CornerCurveOptions
) {
  if (r <= 0) return;

  if (opts.type === "smooth") {
    const k = 0.5522847498 * opts.smoothness + (1 - opts.smoothness) * 0.3;
    const kR = k * r;

    let cp1x: number, cp1y: number, cp2x: number, cp2y: number, ex: number, ey: number;

    switch (startAngle) {
      case 0: // top-right
        cp1x = cx + kR; cp1y = cy - r;
        cp2x = cx + r; cp2y = cy - kR;
        ex = cx + r; ey = cy;
        break;
      case 90: // bottom-right
        cp1x = cx + r; cp1y = cy + kR;
        cp2x = cx + kR; cp2y = cy + r;
        ex = cx; ey = cy + r;
        break;
      case 180: // bottom-left
        cp1x = cx - kR; cp1y = cy + r;
        cp2x = cx - r; cp2y = cy + kR;
        ex = cx - r; ey = cy;
        break;
      case 270: // top-left
        cp1x = cx - r; cp1y = cy - kR;
        cp2x = cx - kR; cp2y = cy - r;
        ex = cx; ey = cy - r;
        break;
      default:
        return;
    }

    p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
  }
}

export function roundedRectPath2D(w: number, h: number, r: CornerRadiiPx, cornerOpts?: CornerCurveOptions): Path2D {
  const [rtl, rtr, rbr, rbl] = r;
  const opts = cornerOpts || { type: "circular", smoothness: 0.6 };
  const p = new Path2D();

  p.moveTo(rtl, 0);
  p.lineTo(w - rtr, 0);

  // Top-right corner
  if (rtr > 0) {
    if (opts.type === "cut") {
      p.lineTo(w, rtr);
    } else if (opts.type === "smooth") {
      drawCornerPath2D(p, w - rtr, rtr, rtr, 0, opts);
    } else {
      p.arcTo(w, 0, w, rtr, rtr);
    }
  } else {
    p.lineTo(w, 0);
  }

  p.lineTo(w, h - rbr);

  // Bottom-right corner
  if (rbr > 0) {
    if (opts.type === "cut") {
      p.lineTo(w - rbr, h);
    } else if (opts.type === "smooth") {
      drawCornerPath2D(p, w - rbr, h - rbr, rbr, 90, opts);
    } else {
      p.arcTo(w, h, w - rbr, h, rbr);
    }
  } else {
    p.lineTo(w, h);
  }

  p.lineTo(rbl, h);

  // Bottom-left corner
  if (rbl > 0) {
    if (opts.type === "cut") {
      p.lineTo(0, h - rbl);
    } else if (opts.type === "smooth") {
      drawCornerPath2D(p, rbl, h - rbl, rbl, 180, opts);
    } else {
      p.arcTo(0, h, 0, h - rbl, rbl);
    }
  } else {
    p.lineTo(0, h);
  }

  p.lineTo(0, rtl);

  // Top-left corner
  if (rtl > 0) {
    if (opts.type === "cut") {
      p.lineTo(rtl, 0);
    } else if (opts.type === "smooth") {
      drawCornerPath2D(p, rtl, rtl, rtl, 270, opts);
    } else {
      p.arcTo(0, 0, rtl, 0, rtl);
    }
  } else {
    p.lineTo(0, 0);
  }

  p.closePath();
  return p;
}

function edgeBulgeVec(type: Exclude<CurveType, "full-arc" | "custom-bezier">) {
  switch (type) {
    case "top":
      return { dx: 0, dy: -1 };
    case "bottom":
      return { dx: 0, dy: 1 };
    case "left":
      return { dx: -1, dy: 0 };
    case "right":
      return { dx: 1, dy: 0 };
  }
}

function bezierEdge(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  bulgeDx: number,
  bulgeDy: number
) {
  const cp1x = x1 + (x2 - x1) / 3 + bulgeDx;
  const cp1y = y1 + (y2 - y1) / 3 + bulgeDy;
  const cp2x = x1 + ((x2 - x1) * 2) / 3 + bulgeDx;
  const cp2y = y1 + ((y2 - y1) * 2) / 3 + bulgeDy;
  ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y2);
}

/**
 * Rounded-rect path with a single-edge curve warp applied after corner radii.
 * This affects outline only (used for clipping + border stroke).
 */
export function roundedRectWithCurvePath(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  r: CornerRadiiPx,
  curve: NormalizedCurve | null,
  cornerOpts?: CornerCurveOptions
) {
  if (!curve) return roundedRectPath(ctx, w, h, r, cornerOpts);

  const [rtl, rtr, rbr, rbl] = r;
  const minDim = Math.max(1, Math.min(w, h));
  const maxAmp = minDim * 0.35;
  const dir = curve.direction === "out" ? 1 : -1;

  const applyTop = curve.type === "top" || curve.type === "full-arc";
  const applyBottom = curve.type === "bottom" || curve.type === "full-arc" || curve.type === "custom-bezier";
  const applyLeft = curve.type === "left";
  const applyRight = curve.type === "right";

  ctx.beginPath();
  ctx.moveTo(rtl, 0);

  // Top edge: (rtl,0) -> (w-rtr,0)
  if (applyTop) {
    const { dx, dy } = edgeBulgeVec("top");
    const amp = dir * curve.strength * maxAmp;
    bezierEdge(ctx, rtl, 0, w - rtr, 0, dx * amp, dy * amp);
  } else {
    ctx.lineTo(w - rtr, 0);
  }

  // Top-right corner
  if (rtr > 0) ctx.arcTo(w, 0, w, rtr, rtr);
  else ctx.lineTo(w, 0);

  // Right edge: (w,rtr) -> (w,h-rbr)
  if (applyRight) {
    const { dx, dy } = edgeBulgeVec("right");
    const amp = dir * curve.strength * maxAmp;
    bezierEdge(ctx, w, rtr, w, h - rbr, dx * amp, dy * amp);
  } else {
    ctx.lineTo(w, h - rbr);
  }

  // Bottom-right corner
  if (rbr > 0) ctx.arcTo(w, h, w - rbr, h, rbr);
  else ctx.lineTo(w, h);

  // Bottom edge: (w-rbr,h) -> (rbl,h)
  if (applyBottom) {
    const amp = dir * curve.strength * maxAmp;
    if (curve.type === "custom-bezier" && curve.bezier) {
      const x1 = w - rbr;
      const y1 = h;
      const x2 = rbl;
      const y2 = h;
      const len = x2 - x1;
      const { dx, dy } = edgeBulgeVec("bottom");
      const bulgeDx = dx * amp;
      const bulgeDy = dy * amp;
      const cp1x = x1 + len * curve.bezier.c1x + bulgeDx;
      const cp1y = y1 + (curve.bezier.c1y - 1) * maxAmp * dir + bulgeDy;
      const cp2x = x1 + len * curve.bezier.c2x + bulgeDx;
      const cp2y = y1 + (curve.bezier.c2y - 1) * maxAmp * dir + bulgeDy;
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y2);
    } else {
      const { dx, dy } = edgeBulgeVec("bottom");
      bezierEdge(ctx, w - rbr, h, rbl, h, dx * amp, dy * amp);
    }
  } else {
    ctx.lineTo(rbl, h);
  }

  // Bottom-left corner
  if (rbl > 0) ctx.arcTo(0, h, 0, h - rbl, rbl);
  else ctx.lineTo(0, h);

  // Left edge: (0,h-rbl) -> (0,rtl)
  if (applyLeft) {
    const { dx, dy } = edgeBulgeVec("left");
    const amp = dir * curve.strength * maxAmp;
    bezierEdge(ctx, 0, h - rbl, 0, rtl, dx * amp, dy * amp);
  } else {
    ctx.lineTo(0, rtl);
  }

  // Top-left corner
  if (rtl > 0) ctx.arcTo(0, 0, rtl, 0, rtl);
  else ctx.lineTo(0, 0);

  ctx.closePath();
}

export function roundedRectWithCurvePath2D(w: number, h: number, r: CornerRadiiPx, curve: NormalizedCurve | null, cornerOpts?: CornerCurveOptions): Path2D {
  if (!curve) return roundedRectPath2D(w, h, r, cornerOpts);

  const [rtl, rtr, rbr, rbl] = r;
  const minDim = Math.max(1, Math.min(w, h));
  const maxAmp = minDim * 0.35;
  const dir = curve.direction === "out" ? 1 : -1;

  const applyTop = curve.type === "top" || curve.type === "full-arc";
  const applyBottom = curve.type === "bottom" || curve.type === "full-arc" || curve.type === "custom-bezier";
  const applyLeft = curve.type === "left";
  const applyRight = curve.type === "right";

  const p = new Path2D();
  p.moveTo(rtl, 0);

  // Top edge: (rtl,0) -> (w-rtr,0)
  if (applyTop) {
    const { dx, dy } = edgeBulgeVec("top");
    const amp = dir * curve.strength * maxAmp;
    const cp1x = rtl + (w - rtr - rtl) / 3 + dx * amp;
    const cp1y = 0 + dy * amp;
    const cp2x = rtl + ((w - rtr - rtl) * 2) / 3 + dx * amp;
    const cp2y = 0 + dy * amp;
    p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, w - rtr, 0);
  } else {
    p.lineTo(w - rtr, 0);
  }

  // Top-right corner
  if (rtr > 0) p.arcTo(w, 0, w, rtr, rtr);
  else p.lineTo(w, 0);

  // Right edge: (w,rtr) -> (w,h-rbr)
  if (applyRight) {
    const { dx, dy } = edgeBulgeVec("right");
    const amp = dir * curve.strength * maxAmp;
    const cp1x = w + dx * amp;
    const cp1y = rtr + (h - rbr - rtr) / 3 + dy * amp;
    const cp2x = w + dx * amp;
    const cp2y = rtr + ((h - rbr - rtr) * 2) / 3 + dy * amp;
    p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, w, h - rbr);
  } else {
    p.lineTo(w, h - rbr);
  }

  // Bottom-right corner
  if (rbr > 0) p.arcTo(w, h, w - rbr, h, rbr);
  else p.lineTo(w, h);

  // Bottom edge: (w-rbr,h) -> (rbl,h)
  if (applyBottom) {
    const amp = dir * curve.strength * maxAmp;
    if (curve.type === "custom-bezier" && curve.bezier) {
      const x1 = w - rbr;
      const y1 = h;
      const x2 = rbl;
      const len = x2 - x1;
      const { dx, dy } = edgeBulgeVec("bottom");
      const bulgeDx = dx * amp;
      const bulgeDy = dy * amp;
      const cp1x = x1 + len * curve.bezier.c1x + bulgeDx;
      const cp1y = y1 + (curve.bezier.c1y - 1) * maxAmp * dir + bulgeDy;
      const cp2x = x1 + len * curve.bezier.c2x + bulgeDx;
      const cp2y = y1 + (curve.bezier.c2y - 1) * maxAmp * dir + bulgeDy;
      p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y1);
    } else {
      const { dx, dy } = edgeBulgeVec("bottom");
      const x1 = w - rbr;
      const x2 = rbl;
      const ampX = dx * amp;
      const ampY = dy * amp;
      const cp1x = x1 + (x2 - x1) / 3 + ampX;
      const cp1y = h + ampY;
      const cp2x = x1 + ((x2 - x1) * 2) / 3 + ampX;
      const cp2y = h + ampY;
      p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, h);
    }
  } else {
    p.lineTo(rbl, h);
  }

  // Bottom-left corner
  if (rbl > 0) p.arcTo(0, h, 0, h - rbl, rbl);
  else p.lineTo(0, h);

  // Left edge: (0,h-rbl) -> (0,rtl)
  if (applyLeft) {
    const { dx, dy } = edgeBulgeVec("left");
    const amp = dir * curve.strength * maxAmp;
    const cp1x = 0 + dx * amp;
    const cp1y = h - rbl + (rtl - (h - rbl)) / 3 + dy * amp;
    const cp2x = 0 + dx * amp;
    const cp2y = h - rbl + ((rtl - (h - rbl)) * 2) / 3 + dy * amp;
    p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, 0, rtl);
  } else {
    p.lineTo(0, rtl);
  }

  // Top-left corner
  if (rtl > 0) p.arcTo(0, 0, rtl, 0, rtl);
  else p.lineTo(0, 0);

  p.closePath();
  return p;
}
